/**
 * Game settings — structured forms over the `game_settings` key/value table.
 *
 * Sections: Company, Commissions, Feature Flags, Invoice, Economy, and a
 * raw-key fallback for anything that does not fit the above. Meta tags are
 * hidden (edited on the Meta tags tab) and payment method toggles live on
 * the Payments tab.
 */
import {
  getCompany, getCommissions, getSettings, setCompany, setCommissions, setSetting,
} from '../admin-api';
import type { CommissionsMap, CompanyInfo, SettingsMap } from '../admin-api.types';
import { checkbox, errorText, esc, field, readForm, section } from '../admin-kit';
import type { AdminTab } from '../admin-kit';

// ─── Prefixes hidden from the raw fallback ──────────────────────────────────

const HIDDEN_PREFIXES = ['meta.', 'company.', 'commission.', 'feature.', 'invoice.', 'payments.', 'economy.'];
const HIDDEN_EXACT = new Set(['payment_methods']);

function isHiddenKey(key: string): boolean {
  return HIDDEN_PREFIXES.some((p) => key.startsWith(p)) || HIDDEN_EXACT.has(key);
}

// ─── Company section ────────────────────────────────────────────────────────

interface CompanyField { key: string & keyof CompanyInfo; label: string; hint?: string }
const COMPANY_FIELDS: CompanyField[] = [
  { key: 'name', label: 'Company name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'website', label: 'Website' },
  { key: 'addressLine1', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'postalCode', label: 'Postal code' },
  { key: 'country', label: 'Country', hint: '2-letter ISO code (e.g. EE, DE)' },
  { key: 'registrationNumber', label: 'Reg. number' },
  { key: 'vatNumber', label: 'VAT number' },
  { key: 'bankName', label: 'Bank name' },
  { key: 'bankAccount', label: 'IBAN / account' },
  { key: 'bankSwift', label: 'SWIFT / BIC' },
];

function companyForm(data: CompanyInfo): string {
  return `<form class="tg-adm-form" data-form="company">
    <h3 class="tg-adm-label">Company</h3>
    <div class="tg-adm-grid">
      ${COMPANY_FIELDS.map((f) => field(f.key, f.label, data[f.key] ?? '', { hint: f.hint })).join('')}
    </div>
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save company</button>
    </div>
  </form>`;
}

// ─── Commissions section ────────────────────────────────────────────────────

interface CommField { key: string; label: string; hint: string }
const COMMISSION_FIELDS: CommField[] = [
  { key: 'deposit_bps', label: 'Deposit commission', hint: 'basis points (500 = 5%)' },
  { key: 'purchase_bps', label: 'Purchase commission', hint: 'basis points' },
  { key: 'withdrawal_bps', label: 'Withdrawal commission', hint: 'basis points (1000 = 10%)' },
  { key: 'min_withdrawal_cents', label: 'Min withdrawal', hint: 'cents (5000 = €50)' },
];

function commissionsForm(data: CommissionsMap): string {
  return `<form class="tg-adm-form" data-form="commissions">
    <h3 class="tg-adm-label">Commissions</h3>
    <div class="tg-adm-grid">
      ${COMMISSION_FIELDS.map((f) => field(f.key, f.label, data[f.key] ?? '', { type: 'number', hint: f.hint })).join('')}
    </div>
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save commissions</button>
    </div>
  </form>`;
}

// ─── Feature flags section ──────────────────────────────────────────────────

interface FeatureFlag { key: string; label: string }
const FEATURE_FLAGS: FeatureFlag[] = [
  { key: 'feature.kyc', label: 'KYC verification' },
  { key: 'feature.withdrawals', label: 'Withdrawals' },
  { key: 'feature.invoices', label: 'Invoices' },
];

function featuresForm(settings: SettingsMap): string {
  return `<form class="tg-adm-form" data-form="features">
    <h3 class="tg-adm-label">Feature flags</h3>
    ${FEATURE_FLAGS.map((f) => checkbox(f.key, f.label, settings[f.key] === 'true')).join('')}
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save features</button>
    </div>
  </form>`;
}

// ─── Invoice settings section ───────────────────────────────────────────────

interface InvoiceField { key: string; label: string; hint?: string; type?: string }
const INVOICE_FIELDS: InvoiceField[] = [
  { key: 'invoice.prefix', label: 'Invoice prefix', hint: '1–12 chars, A-Z 0-9 dash' },
  { key: 'invoice.credit_note_prefix', label: 'Credit note prefix' },
  { key: 'invoice.redate_backdate_days', label: 'Redate backdate (days)', type: 'number' },
  { key: 'invoice.redate_future_hours', label: 'Redate future (hours)', type: 'number' },
];
const INVOICE_CHECKBOX = { key: 'invoice.email_enabled', label: 'Send invoice by email' };

function invoiceForm(settings: SettingsMap): string {
  return `<form class="tg-adm-form" data-form="invoice">
    <h3 class="tg-adm-label">Invoices</h3>
    <div class="tg-adm-grid">
      ${INVOICE_FIELDS.map((f) => field(f.key, f.label, settings[f.key] ?? '', { type: f.type, hint: f.hint })).join('')}
    </div>
    ${checkbox(INVOICE_CHECKBOX.key, INVOICE_CHECKBOX.label, settings[INVOICE_CHECKBOX.key] === 'true')}
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save invoice settings</button>
    </div>
  </form>`;
}

// ─── Economy / misc settings section ────────────────────────────────────────

function economyForm(settings: SettingsMap): string {
  const currency = settings['economy.admin_grant_currency'] ?? 'PREMIUM';
  const requireKyc = settings['payments.require_kyc_for_deposits'] === 'true';
  return `<form class="tg-adm-form" data-form="economy">
    <h3 class="tg-adm-label">Economy</h3>
    <label class="tg-adm-field">
      <span class="tg-adm-label">Admin grant currency</span>
      <select name="economy.admin_grant_currency">
        <option value="SOFT" ${currency === 'SOFT' ? 'selected' : ''}>SOFT</option>
        <option value="PREMIUM" ${currency === 'PREMIUM' ? 'selected' : ''}>PREMIUM</option>
      </select>
    </label>
    ${checkbox('payments.require_kyc_for_deposits', 'Require KYC for deposits', requireKyc)}
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save economy</button>
    </div>
  </form>`;
}

// ─── Raw fallback for unrecognised keys ─────────────────────────────────────

function rawForm(settings: SettingsMap): string {
  const entries = Object.entries(settings)
    .filter(([key]) => !isHiddenKey(key))
    .sort(([a], [b]) => a.localeCompare(b));

  if (!entries.length) return '';

  return `<form class="tg-adm-form" data-form="raw">
    <h3 class="tg-adm-label">Other</h3>
    ${entries.map(([key, value]) => `
      <label class="tg-adm-field">
        <span class="tg-adm-label tg-adm-mono">${esc(key)}</span>
        <input name="${esc(key)}" value="${esc(value)}" data-initial="${esc(value)}">
      </label>`).join('')}
    <div class="tg-adm-actions">
      <button type="submit" class="tg-adm-btn tg-adm-btn-primary">Save other</button>
    </div>
  </form>`;
}

// ─── Tab ────────────────────────────────────────────────────────────────────

export const settingsTab: AdminTab = {
  id: 'settings',
  label: 'Settings',
  group: 'CMS',

  mount(host, ctx) {
    let live = true;
    let company: CompanyInfo | null = null;
    let commissions: CommissionsMap | null = null;
    let settings: SettingsMap | null = null;
    let error = '';

    function body(): string {
      if (error) return `<p class="tg-adm-empty">${esc(error)}</p>`;
      if (!company || !commissions || !settings) return '<p class="tg-adm-empty">Loading…</p>';

      return [
        companyForm(company),
        commissionsForm(commissions),
        featuresForm(settings),
        invoiceForm(settings),
        economyForm(settings),
        rawForm(settings),
      ].filter(Boolean).join('<hr class="tg-adm-divider">');
    }

    const paint = () => {
      host.innerHTML = section(
        'Settings',
        'Company info, commissions, feature flags, and invoice configuration.',
        body(),
        '<button type="button" class="tg-adm-btn" data-act="reload">Reload</button>',
      );
    };

    async function load(): Promise<void> {
      error = '';
      try {
        const [c, cm, s] = await Promise.all([getCompany(), getCommissions(), getSettings()]);
        if (!live) return;
        company = c;
        commissions = cm;
        settings = s;
      } catch (err) {
        if (!live) return;
        error = errorText(err);
      }
      paint();
    }

    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-act="reload"]')) void load();
    };

    async function handleCompany(form: HTMLFormElement): Promise<void> {
      const data = readForm(form) as Record<string, string>;
      try {
        await setCompany(data);
        if (!live) return;
        ctx.notify('Company saved.');
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    async function handleCommissions(form: HTMLFormElement): Promise<void> {
      const data = readForm(form) as Record<string, string>;
      const nums: Record<string, number> = {};
      for (const [k, v] of Object.entries(data)) {
        const n = Number(v);
        if (!Number.isFinite(n)) { ctx.notify(`"${k}" must be a number.`, 'err'); return; }
        nums[k] = n;
      }
      try {
        await setCommissions(nums);
        if (!live) return;
        ctx.notify('Commissions saved.');
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    async function handleKeyValue(form: HTMLFormElement, label: string): Promise<void> {
      const data = readForm(form);
      const pairs: [string, string][] = [];
      for (const [key, value] of Object.entries(data)) {
        pairs.push([key, typeof value === 'boolean' ? String(value) : value]);
      }
      if (!pairs.length) { ctx.notify('Nothing to save.'); return; }
      try {
        for (const [key, value] of pairs) await setSetting(key, value);
        if (!live) return;
        ctx.notify(`${label} saved.`);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    async function handleRaw(form: HTMLFormElement): Promise<void> {
      const changed = Array.from(form.querySelectorAll<HTMLInputElement>('input[name]'))
        .filter((input) => input.value !== input.dataset.initial);
      if (!changed.length) { ctx.notify('Nothing changed.'); return; }
      try {
        for (const input of changed) await setSetting(input.name, input.value);
        if (!live) return;
        ctx.notify(`Saved ${changed.length} setting${changed.length === 1 ? '' : 's'}.`);
      } catch (err) {
        if (!live) return;
        ctx.notify(errorText(err), 'err');
      }
      await load();
    }

    const onSubmit = (e: SubmitEvent) => {
      const form = e.target as HTMLFormElement;
      const kind = form.dataset.form;
      if (!kind) return;
      e.preventDefault();

      switch (kind) {
        case 'company': void handleCompany(form); break;
        case 'commissions': void handleCommissions(form); break;
        case 'features': void handleKeyValue(form, 'Features'); break;
        case 'invoice': void handleKeyValue(form, 'Invoice settings'); break;
        case 'economy': void handleKeyValue(form, 'Economy'); break;
        case 'raw': void handleRaw(form); break;
      }
    };

    host.addEventListener('click', onClick);
    host.addEventListener('submit', onSubmit as EventListener);

    paint();
    void load();

    return () => {
      live = false;
      host.removeEventListener('click', onClick);
      host.removeEventListener('submit', onSubmit as EventListener);
    };
  },
};
